import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICoach } from '../coach/coachSlice';

export enum Role {
  PRINCIPAL_COACH = 'Principal Coach',
  ASSISTANT_COACH = 'Assistant Coach',
}

export interface IGetCoachGroupPayload {
  skip: number;
  take: number;
  isactiveCoaches: boolean;
  filterText?: string;
}

export interface IAddCoachToGroupInput {
  firstName: string;
  lastName?: string;
  email: string;
  tp?: string;
  organizationId: string;
  brandTag?: string;
  role: string;
}

export interface ICoachGroupMembers {
  coaches: ICoach[];
  totalCount: number;
}

export interface ICoachGroup {
  id: string;
  name: string;
  principalCoach: ICoach | null;
  coaches: ICoach[];
  totalCount: number;
}

export interface IEditCoachToGroupInput {
  id: string;
  firstName: string;
  lastName?: string;
  email: string;
  tp?: string;
  organizationId: string;
  brandTag?: string;
  role: string;
}

interface ICoachGroupState {
  coachGroup: ICoachGroup;
  getCoachGroupLoading: boolean;
  addCoachToGroupLoading: boolean;
  getCoachGroupError: string | null;
  addCoachToGroupError: string | null;
  isAddMemberSuccess: boolean;
  fetchArchivedTeamMembersLoading: boolean;
  archivedTeamMembers: ICoach[];
  editCoachInoGroupLoading: boolean;
}

const initialState: ICoachGroupState = {
  coachGroup: {
    id: '',
    name: '',
    principalCoach: null,
    coaches: [],
    totalCount: 0,
  },
  getCoachGroupLoading: false,
  addCoachToGroupLoading: false,
  getCoachGroupError: null,
  addCoachToGroupError: null,
  isAddMemberSuccess: false,
  fetchArchivedTeamMembersLoading: false,
  archivedTeamMembers: [],
  editCoachInoGroupLoading: false,
};

const coachGroupSlice = createSlice({
  name: 'coachGroup',
  initialState,
  reducers: {
    fetchCoachGroupRequest: (
      state: ICoachGroupState,
      _action: PayloadAction<IGetCoachGroupPayload>
    ) => {
      state.getCoachGroupLoading = true;
    },
    fetchCoachGroupSuccess: (state: ICoachGroupState, action: PayloadAction<ICoachGroup>) => {
      state.getCoachGroupLoading = false;
      state.coachGroup = action.payload;
    },
    fetchCoachGroupFailure: (state: ICoachGroupState, action: PayloadAction<string>) => {
      state.getCoachGroupLoading = false;
      state.getCoachGroupError = action.payload;
    },
    fetchArchivedTeamMembers: (
      state: ICoachGroupState,
      _action: PayloadAction<IGetCoachGroupPayload>
    ) => {
      state.fetchArchivedTeamMembersLoading = true;
    },
    fetchArchivedTeamMembersSuccess: (state: ICoachGroupState, action) => {
      state.fetchArchivedTeamMembersLoading = false;
      state.archivedTeamMembers = action.payload.coaches;
    },
    fetchArchivedTeamMembersFailure: (state: ICoachGroupState, action: PayloadAction<string>) => {
      state.fetchArchivedTeamMembersLoading = false;
      state.getCoachGroupError = action.payload;
    },
    addCoachToGroupRequest: (
      state: ICoachGroupState,
      _action: PayloadAction<IAddCoachToGroupInput>
    ) => {
      state.addCoachToGroupLoading = true;
    },
    addCoachToGroupSuccess: (state: ICoachGroupState, action: PayloadAction<ICoachGroup>) => {
      state.addCoachToGroupLoading = false;
      state.coachGroup = action.payload;
      state.isAddMemberSuccess = true;
    },
    closeAddMemberModalOnSuccess: (state: ICoachGroupState) => {
      state.isAddMemberSuccess = false;
    },
    addCoachToGroupFailure: (state: ICoachGroupState, action: PayloadAction<string>) => {
      state.addCoachToGroupLoading = false;
      state.addCoachToGroupError = action.payload;
    },
    resetMembersTableData: (state: ICoachGroupState) => {
      state.coachGroup = {
        id: '',
        name: '',
        principalCoach: null,
        coaches: [],
        totalCount: 0,
      };
      state.getCoachGroupLoading = false;
      state.addCoachToGroupLoading = false;
    },
    editTeamMemberDataRequest: (
      state: ICoachGroupState,
      _action: PayloadAction<IEditCoachToGroupInput>
    ) => {
      state.editCoachInoGroupLoading = true;
    },
    editTeamMemberDataSuccess: (state: ICoachGroupState, action: PayloadAction<ICoachGroup>) => {
      state.editCoachInoGroupLoading = false;
      state.coachGroup.coaches = action.payload.coaches;
      state.isAddMemberSuccess = true;
    },
    editTeamMemberDataFailure: (state: ICoachGroupState, action: PayloadAction<string>) => {
      state.editCoachInoGroupLoading = false;
      state.addCoachToGroupError = action.payload;
    },
  },
});

export const {
  fetchCoachGroupRequest,
  fetchCoachGroupSuccess,
  fetchCoachGroupFailure,
  addCoachToGroupRequest,
  addCoachToGroupSuccess,
  addCoachToGroupFailure,
  resetMembersTableData,
  closeAddMemberModalOnSuccess,
  fetchArchivedTeamMembers,
  fetchArchivedTeamMembersSuccess,
  fetchArchivedTeamMembersFailure,
  editTeamMemberDataRequest,
  editTeamMemberDataSuccess,
  editTeamMemberDataFailure,
} = coachGroupSlice.actions;

const coachGroupActions = coachGroupSlice.actions;
const coachGroupReducer = coachGroupSlice.reducer;

export { coachGroupActions, coachGroupReducer };
